/* eslint-disable react/no-unstable-nested-components */
import { ColumnDef, Row } from "@tanstack/react-table";
import * as React from "react";
import {
    Badge,
    Button,
    Card,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    MoreButton,
    Pagination,
    SearchInput,
    Table,
    Tag,
} from "@jhool-io/fe-components";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useIsMutating } from "@tanstack/react-query";
import {
    IProvider,
    ProviderRole,
    ProviderStatus,
} from "../../types/providers.types";
import { APP_COLORS, LISTS_DEFAULT_LIMIT } from "../../../../utils/constants";
import {
    displayNameInRightFormat,
    formatDate,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
    truncateString,
} from "../../../../utils/helpers";
import { UserPermisions } from "../../../../utils/types/user";
import ListState from "../../../../components/ListState/ListState";
import { useDebounce } from "../../../../hooks/helpers";
import { useFetchProviders } from "../../hooks/providers.queries";
import EditProvider from "../EditProvider/EditProvider";
import { ProviderCredential } from "../../../clients/types/clients.types";
import Navbar from "../../../../components/Navbar/Navbar";
import MobileListItem from "../../../../shared-ui/MobileListItem/MobileListItem";
import EditIcon from "../../../../components/Icons/Edit";
import { useAppSelector } from "../../../../hooks/useRedux";
import AddProvider from "../AddProvider/AddProvider";
import Loader from "../../../../shared-ui/Loader/Loader";
import OverrideSupervisorForm from "../OverrideSupervisorForm/OverrideSupervisorForm";
import { NoteCategory } from "../../../notes/types/notes.types";

type Dialogs =
    | "add-provider"
    | "edit-provider"
    | "manage-insurance-overrides"
    | "manage-category-overrides";

export default function ProvidersList() {
    const [dialogInView, setDialogInView] = React.useState<Dialogs | null>(
        null
    );
    const [selectedProviderId, setSelectedProviderId] = React.useState<
        string | null
    >(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const [sortConfig, setSortConfig] = React.useState<{
        key: string;
        direction: string;
    }>({
        key: "",
        direction: "",
    });

    const { practice } = useAppSelector((state) => state.userPractice);
    const canEditProviderInfo = practice?.permissions?.includes(
        UserPermisions.PROVIDER_INFO_UPDATE
    );
    const canManageSupervisors = practice?.permissions?.includes(
        UserPermisions.PROVIDER_SUPERVISOR_OVERRIDE_UPDATE
    );
    const canViewProviderActions = canEditProviderInfo || canManageSupervisors;

    const sortableColumns = [
        "provider_name",
        "contact",
        "email",
        "no_of_clients",
        "no_of_supervisees",
        "supervisor_name",
    ];

    const handleConfigAttribute = () => {
        if (sortableColumns.includes(sortConfig.key)) {
            return sortConfig.key;
        }

        return undefined;
    };

    const searchString = searchParams.get("search") || "";
    const statusFilter = searchParams.get("status") || undefined;
    const roleFilter = searchParams.get("role") || undefined;
    const credentialFilter = searchParams.get("credential") || undefined;
    const pageFilter = Number(searchParams.get("page")) || 1;
    const limitFilter =
        Number(searchParams.get("limit")) || LISTS_DEFAULT_LIMIT;

    const name = useDebounce(searchString, 500);

    const { data, isLoading, isSuccess, error } = useFetchProviders({
        page: pageFilter,
        limit: limitFilter,
        status: statusFilter,
        role: roleFilter || "",
        search_string: name,
        credential: credentialFilter,
        sort_attribute:
            searchParams.get("sort_attr") || handleConfigAttribute(),
        sort_order: searchParams.get("sort_order") || sortConfig.direction,
    });

    // Get provider list count
    let providersCount;
    if (error) providersCount = "";
    else if (isLoading) providersCount = "";
    else if (data?.data && data?.data.length === 0) providersCount = 0;
    else if (data?.data && data?.data.length > 0)
        providersCount = data.total_count;

    const isEditMutationRunning = useIsMutating(["update-provider-info"]);

    const isAddingProvider = useIsMutating(["add-users"]);
    const isOverridingSupervisor = useIsMutating(["override-supervisor"]);

    // Details for a single provider
    const providerDetails = data?.data.find((provider) => {
        return provider.provider_id === selectedProviderId;
    });

    const credentialOptionsForSelect = Object.values(ProviderCredential).map(
        (status) => {
            return status;
        }
    );

    const statusOptionsForSelect = Object.values(ProviderStatus).map(
        (status) => {
            return status;
        }
    );

    const rolesForSelect = Object.values(ProviderRole).map((status) => {
        return status;
    });

    const navigate = useNavigate();

    const handleRowClick = (row: Row<IProvider>) => {
        navigate(`/providers/${row.original.provider_id as string}`);
    };

    const handleCloseDialog = () => {
        setSelectedProviderId(null);
        setDialogInView(null);
    };

    const handleSetProviderToEdit = (id: string) => {
        setDialogInView("edit-provider");
        setSelectedProviderId(id);
    };

    // onChange handler for search input
    const handleSetSearchString = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value === "") searchParams.delete("search");
        else searchParams.set("search", e.target.value);

        setSearchParams(searchParams);
    };

    const handleStatusChange = (value: string) => {
        if (value === "") searchParams.delete("status");
        else searchParams.set("status", value);
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleCredentialChange = (value: string) => {
        if (value === "") searchParams.delete("credential");
        else searchParams.set("credential", value);
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleRoleChange = (value: string) => {
        if (value === "") searchParams.delete("role");
        else searchParams.set("role", value);
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    // onChange handler for page
    const handlePageChange = (page: number) => {
        searchParams.set("page", String(page));
        setSearchParams(searchParams);
    };

    // onChange handler for limit
    const handleLimitChange = (limit: number) => {
        searchParams.set("limit", String(limit));
        setSearchParams(searchParams);
    };

    // Provider status tag
    const getStatusTag = (status: ProviderStatus) => {
        let bgColor: string;
        let textColor: string;

        switch (status) {
            case ProviderStatus.ACTIVE:
                textColor = "#00563E";
                bgColor = "rgba(204, 250, 233, 0.50)";
                break;
            case ProviderStatus.INACTIVE:
                textColor = "#981F41";
                bgColor = "rgba(251, 199, 198, 0.50)";
                break;
            default:
                bgColor = "rgba(245, 245, 245, 0.50)";
                textColor = APP_COLORS.COLOR_BLACK;
                break;
        }

        return (
            <Tag
                textColor={textColor}
                bgColor={bgColor}
                title={makeStringFirstLetterCapital(status)}
                className="capitalize w-auto h-auto text-nowrap px-8 py-4 rounded-r4"
            />
        );
    };

    const getCredentialTag = (credential: ProviderCredential) => {
        let bgColor: string;
        let textColor: string;

        switch (credential) {
            case ProviderCredential.LMSW:
            case ProviderCredential.MHCLP:
                textColor = "var(--color-warning-text)";
                bgColor = "var(--color-warning-bg)";
                break;
            case ProviderCredential.LMSWA:
            case ProviderCredential.LMHCA:
            case ProviderCredential.PSYDA:
            case ProviderCredential.PHDA:
            case ProviderCredential.LCSWA:
                textColor = "var(--color-info-text)";
                bgColor = "var(--color-info-bg)";
                break;
            case ProviderCredential.LMHCB:
            case ProviderCredential.PSYDB:
            case ProviderCredential.PHDB:
            case ProviderCredential.LCSWB:
                textColor = "var(--color-danger-text)";
                bgColor = "var(--color-danger-bg)";
                break;
            case ProviderCredential.LMHCC:
            case ProviderCredential.PSYDC:
            case ProviderCredential.PHDC:
            case ProviderCredential.LCSWC:
                textColor = "var(--color-neutral-text)";
                bgColor = "var(--color-neutral-bg)";
                break;
            default:
                textColor = "var(--color-success-text)";
                bgColor = "var(--color-success-bg)";
                break;
        }

        return (
            <Badge
                content={credential}
                textColor={textColor}
                bgColor={bgColor}
                type="custom"
            />
        );
    };

    const columns: ColumnDef<IProvider>[] = [
        {
            accessorKey: "provider_name",
            header: "PROVIDER'S NAME",
            cell: ({ row }) =>
                displayNameInRightFormat(row.original.provider_name),
        },
        {
            accessorKey: "credential",
            header: "CREDENTIAL",
            cell: ({ row }) =>
                row.original.credential
                    ? getCredentialTag(row.original.credential)
                    : "--",
        },
        {
            accessorKey: "contact",
            header: "CONTACT",
            cell: ({ row }) => row.original.provider_contact_number || "--",
        },
        {
            accessorKey: "email",
            header: "EMAIL",
            cell: ({ row }) => row.original.email || "--",
        },
        {
            accessorKey: "no_of_clients",
            header: "NO OF CLIENTS",
            cell: ({ row }) => row.original.no_of_active_clients || "--",
        },
        {
            accessorKey: "npi",
            header: "NPI",
            cell: ({ row }) => row.original.npi || "--",
        },
        {
            accessorKey: "started_practicing",
            header: "STARTED PRACTICING",
            cell: ({ row }) =>
                row.original.started_practicing
                    ? formatDate(row.original.started_practicing)
                    : "--",
        },
        {
            accessorKey: "no_of_supervisees",
            header: "NO OF SUPERVISEES",
            cell: ({ row }) => row.original.no_of_supervisees || "--",
        },
        {
            accessorKey: "supervisor_name",
            header: "SUPERVISOR NAME",
            cell: ({ row }) => (
                <span>
                    {row.original.supervisor_name
                        ? displayNameInRightFormat(row.original.supervisor_name)
                        : "--"}
                </span>
            ),
        },
        {
            accessorKey: "supervisor_overrides",
            header: "SUPERVISOR overrides",
            cell: ({ row }) => {
                const filteredOverrides =
                    row.original.supervisor_overrides?.filter((override) =>
                        Boolean(override?.supervisor?.user)
                    ) ?? [];
                return (
                    <div className="flex flex-col">
                        {filteredOverrides.length > 0
                            ? filteredOverrides.map((override, index) => {
                                  const hasInsurance =
                                      (override?.insurance_provider_ids
                                          ?.length ?? 0) > 0 ||
                                      (override?.insurance_providers?.length ??
                                          0) > 0;
                                  const ids =
                                      override.insurance_provider_ids ??
                                      override.insurance_providers?.map(
                                          (ip) => ip.insurance_provider_id
                                      ) ??
                                      [];
                                  const overrideKey = [
                                      override.supervisor_id,
                                      override.note_category,
                                      ...ids,
                                  ].join("-");
                                  return (
                                      <div
                                          key={overrideKey}
                                          className={
                                              index <
                                              filteredOverrides.length - 1
                                                  ? "border-b border-strokedark pb-2 mb-2"
                                                  : ""
                                          }
                                      >
                                          <div className="text-caption flex items-center gap-x-1">
                                              {displayNameInRightFormat({
                                                  firstName:
                                                      override.supervisor.user
                                                          .first_name,
                                                  lastName:
                                                      override.supervisor.user
                                                          .last_name,
                                              })}
                                              <Badge
                                                  type={
                                                      override.note_category ===
                                                      NoteCategory.TESTING
                                                          ? "info"
                                                          : "neutral"
                                                  }
                                                  content={
                                                      override.note_category ===
                                                      NoteCategory.TESTING
                                                          ? "PSYCH"
                                                          : "Therapy"
                                                  }
                                              />
                                              {hasInsurance && (
                                                  <Badge
                                                      type="warning"
                                                      content="INS"
                                                  />
                                              )}
                                          </div>
                                          {hasInsurance &&
                                              override.insurance_providers &&
                                              override.insurance_providers
                                                  .length > 0 && (
                                                  <div className="text-caption text-gray flex flex-wrap gap-x-1">
                                                      {override.insurance_providers.map(
                                                          (ip) => (
                                                              <span
                                                                  key={
                                                                      ip.insurance_provider_id
                                                                  }
                                                              >
                                                                  {ip.name}
                                                              </span>
                                                          )
                                                      )}
                                                  </div>
                                              )}
                                      </div>
                                  );
                              })
                            : "--"}
                    </div>
                );
            },
        },
        {
            accessorKey: "status",
            header: "PROVIDER STATUS",
            cell: ({ row }) =>
                row.original.status === ProviderStatus.ACTIVE ? (
                    <Badge type="success" content="Active" />
                ) : (
                    <Badge type="danger" content="Inactive" />
                ),
        },
        {
            accessorKey: "provider_id",
            header: "",
            cell: ({ row }) =>
                canViewProviderActions && (
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <MoreButton />
                        </DropdownTrigger>
                        <DropdownContent width="auto" align="end">
                            {canEditProviderInfo && (
                                <DropdownItem
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleSetProviderToEdit(
                                            row.original.provider_id
                                        );
                                    }}
                                >
                                    Edit provider info
                                </DropdownItem>
                            )}
                            {canManageSupervisors && (
                                <>
                                    <DropdownItem
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setDialogInView(
                                                "manage-insurance-overrides"
                                            );
                                            setSelectedProviderId(
                                                row.original.provider_id
                                            );
                                        }}
                                    >
                                        Set supervisors by insurance
                                    </DropdownItem>
                                    <DropdownItem
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setDialogInView(
                                                "manage-category-overrides"
                                            );
                                            setSelectedProviderId(
                                                row.original.provider_id
                                            );
                                        }}
                                    >
                                        Set supervisors by note category
                                    </DropdownItem>
                                </>
                            )}
                        </DropdownContent>
                    </Dropdown>
                ),
        },
    ];

    return (
        <>
            <Dialog open={dialogInView === "edit-provider"}>
                <DialogContent
                    title="Edit provider info"
                    showFooter
                    saveText="Save changes"
                    cancelText="Cancel"
                    variant="center"
                    handleCloseDialog={() => handleCloseDialog()}
                    submitBtnFormValue="update-provider-info"
                    isCancelBtnDisabled={isEditMutationRunning > 0}
                    isSubmitBtnDisabled={isEditMutationRunning > 0}
                >
                    <EditProvider
                        onFormSubmit={() => handleCloseDialog()}
                        providerDetails={providerDetails}
                    />
                </DialogContent>
            </Dialog>
            <Dialog open={dialogInView === "add-provider"}>
                <DialogContent
                    title="Add Provider"
                    showFooter
                    saveText="Add Provider"
                    cancelText="Cancel"
                    variant="center"
                    handleCloseDialog={() => handleCloseDialog()}
                    submitBtnFormValue="add-provider"
                    isCancelBtnDisabled={isAddingProvider > 0}
                    isSubmitBtnDisabled={isAddingProvider > 0}
                >
                    <AddProvider onFormSubmit={() => handleCloseDialog()} />
                </DialogContent>
            </Dialog>
            <Dialog
                open={
                    dialogInView === "manage-insurance-overrides" ||
                    dialogInView === "manage-category-overrides"
                }
            >
                <DialogContent
                    title={
                        providerDetails
                            ? `Set supervisors by ${
                                  dialogInView === "manage-insurance-overrides"
                                      ? "insurance"
                                      : "note category"
                              } for ${providerDetails.provider_name}`
                            : "Set supervisors"
                    }
                    wrapperClass="w-[700px]"
                    showFooter
                    saveText="Save supervisors"
                    variant="center"
                    handleCloseDialog={() => handleCloseDialog()}
                    submitBtnFormValue="override-supervisor"
                    isCancelBtnDisabled={isOverridingSupervisor > 0}
                    isSubmitBtnDisabled={isOverridingSupervisor > 0}
                >
                    <OverrideSupervisorForm
                        onFormSubmit={() => handleCloseDialog()}
                        overrideType={
                            dialogInView === "manage-insurance-overrides"
                                ? "insurance"
                                : "category"
                        }
                        providerDetails={providerDetails}
                    />
                </DialogContent>
            </Dialog>
            <Navbar title={`Providers List (${providersCount})`} />
            <div className="page">
                <Card classNames="flex flex-col gap-y-[--spacing-items] mb-[--spacing-header]">
                    <div className="flex items-center justify-between flex-wrap gap-y-[--spacing-items]">
                        <SearchInput
                            containerClass="w-[400px]"
                            placeholder="Search by name, email or NPI"
                            onChange={handleSetSearchString}
                            defaultValue={searchString}
                        />
                    </div>
                    <div className="flex gap-[--spacing-items] flex-wrap justify-between">
                        <div className="flex items-center gap-[--spacing-items] flex-wrap">
                            <Dropdown>
                                <DropdownTrigger asChild>
                                    <FilterButton>
                                        {statusFilter
                                            ? truncateString(
                                                  removeEnumUnderscore(
                                                      statusFilter
                                                  )
                                              )
                                            : "Status"}
                                    </FilterButton>
                                </DropdownTrigger>
                                <DropdownContent width="auto">
                                    <DropdownItem
                                        isSelected={!statusFilter}
                                        onClick={() => handleStatusChange("")}
                                    >
                                        All
                                    </DropdownItem>
                                    {statusOptionsForSelect.map((status) => (
                                        <DropdownItem
                                            isSelected={statusFilter === status}
                                            key={status}
                                            onClick={() =>
                                                handleStatusChange(status)
                                            }
                                        >
                                            {makeStringFirstLetterCapital(
                                                status
                                            )}
                                        </DropdownItem>
                                    ))}
                                </DropdownContent>
                            </Dropdown>
                            <Dropdown>
                                <DropdownTrigger asChild>
                                    <FilterButton>
                                        {credentialFilter
                                            ? truncateString(
                                                  removeEnumUnderscore(
                                                      credentialFilter
                                                  )
                                              )
                                            : "Credential"}
                                    </FilterButton>
                                </DropdownTrigger>
                                <DropdownContent
                                    width="auto"
                                    className="overflow-y-auto"
                                >
                                    <DropdownItem
                                        isSelected={!credentialFilter}
                                        onClick={() =>
                                            handleCredentialChange("")
                                        }
                                    >
                                        All
                                    </DropdownItem>
                                    {credentialOptionsForSelect.map(
                                        (credential) => (
                                            <DropdownItem
                                                isSelected={
                                                    credentialFilter ===
                                                    credential
                                                }
                                                key={credential}
                                                onClick={() =>
                                                    handleCredentialChange(
                                                        credential
                                                    )
                                                }
                                            >
                                                {makeStringFirstLetterCapital(
                                                    credential
                                                )}
                                            </DropdownItem>
                                        )
                                    )}
                                </DropdownContent>
                            </Dropdown>
                            <Dropdown>
                                <DropdownTrigger asChild>
                                    <FilterButton>
                                        {roleFilter
                                            ? truncateString(
                                                  removeEnumUnderscore(
                                                      roleFilter
                                                  )
                                              )
                                            : "Role"}
                                    </FilterButton>
                                </DropdownTrigger>
                                <DropdownContent width="auto">
                                    <DropdownItem
                                        onClick={() => handleRoleChange("")}
                                        isSelected={!roleFilter}
                                    >
                                        All
                                    </DropdownItem>
                                    {rolesForSelect.map((role) => (
                                        <DropdownItem
                                            isSelected={roleFilter === role}
                                            key={role}
                                            onClick={() =>
                                                handleRoleChange(role)
                                            }
                                        >
                                            {makeStringFirstLetterCapital(role)}
                                        </DropdownItem>
                                    ))}
                                </DropdownContent>
                            </Dropdown>
                        </div>
                        <Button onClick={() => setDialogInView("add-provider")}>
                            Add Provider
                        </Button>
                    </div>
                </Card>
                {isLoading && <Loader />}
                {error && (
                    <ListState
                        isError
                        stateHelperText="Try reloading this page to solve this issue"
                        errorMsg={
                            error?.response?.data.message ||
                            `Cannot display providers list at this time please try again later`
                        }
                    />
                )}
                {data && isSuccess && data.data.length === 0 && (
                    <ListState
                        stateHelperText="Providers list will appear here once providers are added"
                        isError={false}
                        emptyMessage="No providers added yet"
                    />
                )}
                {isSuccess && data && data.data.length > 0 && (
                    <>
                        <div className="">
                            <Table
                                data={data.data}
                                columns={columns}
                                setSortConfig={setSortConfig}
                                sortConfig={sortConfig}
                                isHeaderFixed
                                sortableColumns={sortableColumns}
                                hasPagination={data.total_count > 20}
                                handleRowClick={handleRowClick}
                                pagination={
                                    <Pagination
                                        totalCount={data.total_count}
                                        totalPages={data.total_pages}
                                        currentPage={pageFilter}
                                        onPageChange={handlePageChange}
                                        limit={limitFilter}
                                        onLimitChange={handleLimitChange}
                                    />
                                }
                            />
                        </div>
                        <div role="table" className="hidden">
                            <div className="flex flex-col gap-y-12">
                                {data.data.map((provider) => (
                                    <MobileListItem
                                        key={provider.provider_id}
                                        topChildren={
                                            <div className="flex items-center gap-x-8 text-base font-semibold capitalize">
                                                {displayNameInRightFormat(
                                                    provider.provider_name
                                                )}
                                                <span className=" w-1 h-1 rounded-full bg-gray" />
                                                <span className=" text-sm font-medium">
                                                    {provider.credential}
                                                </span>
                                            </div>
                                        }
                                        bottomChildren={
                                            <Link
                                                className="flex flex-col gap-y-8 w-full items-start"
                                                to={`/providers/${provider.provider_id}`}
                                            >
                                                <div className="flex items-center w-full text-left gap-x-4">
                                                    <span className="w-1/2 text-gray font-semibold text-xs">
                                                        Contact:
                                                    </span>
                                                    <span className="w-1/2 font-semibold text-xs">
                                                        {
                                                            provider.provider_contact_number
                                                        }
                                                    </span>
                                                </div>

                                                <div className="flex items-center w-full text-left gap-x-4">
                                                    <span className="w-1/2 text-gray font-semibold text-xs">
                                                        Email:
                                                    </span>
                                                    <span className="w-1/2 font-semibold text-xs">
                                                        {provider.email}
                                                    </span>
                                                </div>

                                                <div className="flex items-center w-full text-left gap-x-4">
                                                    <span className="w-1/2 text-gray font-semibold text-xs">
                                                        No of clients:
                                                    </span>
                                                    <span className="w-1/2 font-semibold text-xs">
                                                        {
                                                            provider.no_of_active_clients
                                                        }
                                                    </span>
                                                </div>

                                                <div className="flex items-center w-full text-left gap-x-4">
                                                    <span className="w-1/2 text-gray font-semibold text-xs">
                                                        Provider status:
                                                    </span>

                                                    {getStatusTag(
                                                        provider.status
                                                    )}
                                                </div>

                                                <div className="flex items-center w-full text-left gap-x-4">
                                                    <span className="w-1/2 text-gray font-semibold text-xs">
                                                        NPI:
                                                    </span>
                                                    <span className=" flex items-center w-1/2 font-semibold text-xs">
                                                        {provider.npi}

                                                        <Button
                                                            variant="normal"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleSetProviderToEdit(
                                                                    provider.provider_id
                                                                );
                                                            }}
                                                        >
                                                            <EditIcon />
                                                        </Button>
                                                    </span>
                                                </div>
                                                {provider.is_supervisor && (
                                                    <div className="flex items-center w-full text-left gap-x-4">
                                                        <span className="w-1/2 text-gray font-semibold text-xs">
                                                            No of supervisees:
                                                        </span>
                                                        <span className=" w-1/2 font-semibold text-xs">
                                                            {
                                                                provider.no_of_supervisees
                                                            }
                                                        </span>
                                                    </div>
                                                )}
                                                {provider.supervisor_name && (
                                                    <div className="flex items-center w-full text-left gap-x-4">
                                                        <span className="w-1/2 text-gray font-semibold text-xs">
                                                            Supervisor name:
                                                        </span>
                                                        <span className=" w-1/2 font-semibold text-xs">
                                                            {provider.supervisor_name
                                                                ? displayNameInRightFormat(
                                                                      provider.supervisor_name
                                                                  )
                                                                : "--"}
                                                        </span>
                                                    </div>
                                                )}
                                            </Link>
                                        }
                                    />
                                ))}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </>
    );
}
